// import './App.css';
import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import { Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';

// import ReactPixel from "react-facebook-pixel";

import { useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ProtectedRoute } from './routes';

import HistoryMonitor from '~/components/common/HistoryMonitor';
import LanguageMonitor from '~/components/common/LanguageMonitor';
import LoadingSpinner from '~/components/common/LoadingSpinner';
import PageHeaderMeta from '~/components/common/PageHeaderMeta';
import ScrollToTop from '~/components/common/ScrollToTop';
import UpdateCartDB from '~/components/common/UpdateCartDB';
import LayoutClassic from '~/components/layout/Layout';
import AddToWishlist from '~/components/wishlist/AddToWishlist';

// Lazy load page components
const Home = lazy(() => import('~/pages/Home'));
const Logout = lazy(() => import('~/pages/Customer/Logout'));
const Profile = lazy(() => import('~/pages/Customer/Profile'));
const Giftcard = lazy(() => import('~/pages/Giftcards/Giftcard'));
const Account = lazy(() => import('~/pages/Customer/Account'));
// const Coupons = lazy(() => import("./pages/Customer/Coupons"));
const Addresses = lazy(() => import('~/pages/Customer/Addresses'));
const AddressEdit = lazy(() => import('~/pages/Customer/AddressEdit'));
const AddressAdd = lazy(() => import('~/pages/Customer/AddressAdd'));
const OrderHistory = lazy(() => import('~/pages/Customer/OrderHistory'));
const Order = lazy(() => import('~/pages/Customer/Order'));
// const OrderReturn = lazy(() => import("./pages/Customer/OrderReturn"));
const FollowOrder = lazy(() => import('~/pages/Customer/FollowOrder'));
const Register = lazy(() => import('~/pages/Customer/Register'));
const CheckoutStep1 = lazy(() => import('~/pages/Checkout/CheckoutStep1'));
const CheckoutStep2 = lazy(() => import('~/pages/Checkout/CheckoutStep2'));
const CheckoutStep3 = lazy(() => import('~/pages/Checkout/CheckoutStep3'));
const CheckoutSuccess = lazy(() => import('~/pages/Checkout/CheckoutSuccess'));
const CheckoutFailure = lazy(() => import('~/pages/Checkout/CheckoutFailure'));
const RegistrationCheckEmail = lazy(() => import('~/pages/Customer/RegistrationCheckEmail'));
const NotFound = lazy(() => import('~/pages/NotFound'));
const Product = lazy(() => import('~/pages/Product'));
const ContentPage = lazy(() => import('~/pages/ContentPage'));
const RightOfWithdrawal = lazy(() => import('~/pages/RightOfWithdrawal'));
const LoginPage = lazy(() => import('~/pages/LoginPage'));
const CategoryList = lazy(() => import('~/pages/Category/CategoryList'));
const DiscountedList = lazy(() => import('~/pages/Category/DiscountedList'));
const BrandList = lazy(() => import('~/pages/Category/BrandList'));
const Wishlists = lazy(() => import('~/pages/Customer/Wishlists'));
const Wishlist = lazy(() => import('~/pages/Customer/Wishlist'));
const GuestWishlist = lazy(() => import('~/pages/Customer/GuestWishlist'));
const CustomerNavigationPage = lazy(() => import('~/pages/Customer/CustomerNavigationPage'));
const Cart = lazy(() => import('~/pages/Cart'));
const Contact = lazy(() => import('~/pages/Contact'));
const NewsList = lazy(() => import('~/pages/News/NewsList'));
const NewsCategory = lazy(() => import('~/pages/News/NewsCategory'));
const NewsEntry = lazy(() => import('~/pages/News/NewsEntry'));
const StoreFinder = lazy(() => import('~/pages/StoreFinder'));
const ValidateNewsletterSubscription = lazy(() => import('~/pages/ValidateNewsletterSubscription'));
const ValidateCustomerRegistration = lazy(() => import('~/pages/ValidateCustomerRegistration'));
const ResetPasswordRequest = lazy(() => import('~/pages/Customer/ResetPasswordRequest'));
const ResetPassword = lazy(() => import('~/pages/Customer/ResetPassword'));
const Search = lazy(() => import('~/pages/Search'));
const PopularCategories = lazy(() => import('~/pages/PopularCategories'));
const Brands = lazy(() => import('~/pages/Category/Brands'));
const Impersonation = lazy(() => import('~/pages/Impersonation'));
// const Sitemap = lazy(() => import("./pages/Xml/Sitemap"));

// Viva Payments
const VivaSuccess = lazy(() => import('~/pages/VivaPayments/VivaSuccess'));
const VivaFailure = lazy(() => import('~/pages/VivaPayments/VivaFailure'));

// PayPal
const PayPalSuccess = lazy(() => import('~/pages/PayPal/PayPalSuccess'));
const PayPalCancel = lazy(() => import('~/pages/PayPal/PayPalCancel'));

// Alpha Cardlink
const AlphaCardlinkRequestForm = lazy(() => import('~/pages/AlphaBank/AlphaCardlinkRequestForm'));
const AlphaCardlinkSuccess = lazy(() => import('~/pages/AlphaBank/AlphaCardlinkSuccess'));
const AlphaCardlinkCancel = lazy(() => import('~/pages/AlphaBank/AlphaCardlinkCancel'));

const Slt = lazy(() => import('~/pages/Slt'));

function App() {
	// const tagManagerArgs = {
	// 	gtmId: __BW_ENV__.REACT_APP_GTAG_4_ID
	// };

	// TagManager.initialize(tagManagerArgs);

	// const options = {
	// 	autoConfig: true, // set pixel's autoConfig
	// 	debug: false // disable logs
	// };

	// ReactPixel.init(__BW_ENV__.REACT_APP_FACEBOOK_PIXEL_ID, options);

	UpdateCartDB();

	const showAddToWishlist = useSelector(state => state.layoutReducer.showAddToWishlist);
	const wishlistProductId = useSelector(state => state.layoutReducer.wishlistProductId);

	const createAppRoutes = () => {
		return (
			<>
				<Route index element={<Home />} />

				<Route path="pages/right-of-withdrawal" element={<RightOfWithdrawal />} />
				<Route path="pages/:eurl" element={<ContentPage />} />

				<Route path="products/:eurl" element={<Product />} />
				<Route path="products/:eurl/:bcd" element={<Product />} />
				<Route path="categories/:eurl" element={<CategoryList />} />
				<Route path="products/sales" element={<DiscountedList />} />
				<Route path="brands/:eurl" element={<BrandList />} />

				<Route path="brands" element={<Brands />} />
				<Route path="popular-categories" element={<PopularCategories />} />

				<Route path="login" element={<LoginPage />} />
				<Route path="signin" element={<LoginPage />} />
				<Route path="register" element={<LoginPage />} />
				<Route path="logout" element={<Logout />} />

				<Route path="giftcard" element={<Giftcard />} />

				<Route path="cart" element={<Cart />} />

				<Route path="contact" element={<Contact />} />

				<Route path="notfound" element={<NotFound />} />

				<Route path="blog" element={<NewsList />} />
				<Route path="blog/:eurl" element={<NewsCategory />} />
				<Route path="blogPost/:eurl" element={<NewsEntry />} />

				<Route path="checkout-step-1" element={<CheckoutStep1 />} />

				{/*<Route element={<OrderWizardRoute/>}>*/}
				<Route path="checkout-step-2" element={<CheckoutStep2 />} />
				<Route path="checkout-step-3" element={<CheckoutStep3 />} />
				<Route path="checkout-success/:uid" element={<CheckoutSuccess />} />
				<Route path="checkout-failure" element={<CheckoutFailure />} />
				{/*</Route>*/}

				<Route path="register" element={<Register />} />
				<Route path="registrationcheckemail" element={<RegistrationCheckEmail />} />

				<Route path="wishlist/:id" element={<Wishlist />} />
				<Route path="wishlist" element={<GuestWishlist />} />

				<Route path="storefinder" element={<StoreFinder />} />

				<Route path="validatenewslettersubscription/:guid" element={<ValidateNewsletterSubscription />} />
				<Route path="validateregistration/:guid" element={<ValidateCustomerRegistration />} />

				<Route path="resetpasswordrequest" element={<ResetPasswordRequest />} />
				<Route path="resetpassword/:guid" element={<ResetPassword />} />

				<Route path="search" element={<Search />} />

				<Route path="payment/viva/success" element={<VivaSuccess />} />
				<Route path="payment/viva/failure" element={<VivaFailure />} />

				<Route path="payment/paypal/success" element={<PayPalSuccess />} />
				<Route path="payment/paypal/cancel" element={<PayPalCancel />} />

				<Route path="payment/alpha_request/:id" element={<AlphaCardlinkRequestForm />} />
				<Route path="payment/alpha/success/:uid" element={<AlphaCardlinkSuccess />} />
				<Route path="payment/alpha/cancel" element={<AlphaCardlinkCancel />} />

				<Route path="slt/:guid" element={<Slt />} />

				<Route path="impersonate/:guid" element={<Impersonation />} />

				<Route path="*" element={<NotFound />} />

				<Route element={<ProtectedRoute />}>
					<Route path="profile" element={<Profile />} />
					{/* <Route path="coupons" element={<Coupons />} /> */}
					<Route path="addresses" element={<Addresses />} />
					<Route path="address/edit/:id" element={<AddressEdit />} />
					<Route path="address/add" element={<AddressAdd />} />
					<Route path="orders" element={<OrderHistory />} />
					<Route path="order/:uid" element={<Order />} />
					{/* <Route path="order-return/:uid" element={<OrderReturn />} /> */}
					<Route path="account" element={<Account />} />
					<Route path="wishlists" element={<Wishlists />} />
					<Route path="followorder" element={<FollowOrder />} />
					<Route path="followorder/:uid" element={<FollowOrder />} />
					<Route path="navigation" element={<CustomerNavigationPage />} />
				</Route>
			</>
		);
	};

	return (
		<div className="App">
			<Router>
				<HistoryMonitor />
				<LanguageMonitor />
				<PageHeaderMeta
					page_title="UndergroundShop | Streetstyle Ρούχα, Παπούτσια, Αξεσουάρ & Αθλητικά"
					meta_title="UndergroundShop | Streetstyle Ρούχα, Παπούτσια, Αξεσουάρ & Αθλητικά"
					meta_description="Ανακάλυψε τη συλλογή Underground Shop με αυθεντικά streetwear ρούχα, sneakers και αξεσουάρ. Μοναδικό στυλ και ποιότητα για το urban look σου."
				/>
				<ScrollToTop />
				<Suspense fallback={<LoadingSpinner isLoading />}>
					<Routes>
						<Route
							path="/"
							element={
								<LayoutClassic>
									<Outlet />
								</LayoutClassic>
							}>
							{createAppRoutes()}
						</Route>

						<Route
							path="/el"
							element={
								<LayoutClassic>
									<Outlet />
								</LayoutClassic>
							}>
							{createAppRoutes()}
						</Route>

						<Route
							path="/en"
							element={
								<LayoutClassic>
									<Outlet />
								</LayoutClassic>
							}>
							{createAppRoutes()}
						</Route>

						{/* <Route path="/sitemap.xml" element={<Sitemap />} /> */}
					</Routes>
				</Suspense>
			</Router>

			<ToastContainer className="toast-content" />
			{showAddToWishlist == true ? <AddToWishlist productId={wishlistProductId} /> : null}
		</div>
	);
}

App.propTypes = {
	showAddToWishlist: PropTypes.bool,
	wishlistProductId: PropTypes.number
};

export default App;
